import {
  type DragEvent,
  type FunctionComponent,
  type ReactNode,
  type Ref,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from 'react';

import { Button } from '@dr.pogodin/react-utils';

import type { UseExperienceResT } from 'hooks';

import {
  type VideoT,
  getId,
  isInsideRect,
  useGlobalState,
} from 'utils';

import IconArrowLeft from 'assets/images/icon-arrow-left.svg';
import IconEdit from 'assets/images/icon-edit-16px.svg';

import backButtonTheme from 'themes/Button/glass-pill-h56.scss';
import iconButtonTheme from 'themes/Button/icon-32px.scss';

import { VideoCard, addTouchCancelListener, addTouchDropListener } from '../VideoCard';

import { DropZone } from './DropZone';

import './style.scss';

type PropsT = {
  addVideo: (video: VideoT, idx: number | undefined) => void;
  editMode?: boolean;
  experience: UseExperienceResT;
  name?: string;
  onBack?: () => void;
  removeVideo: (idx: number) => void;
  sessionNumber?: number;
  setEditMode: () => void;
  showClipsBrowser?: boolean;
  videosRef: Ref<HTMLDivElement>;
};

const MAX_VISIBLE_INTENTS = 4;

export const Experience: FunctionComponent<PropsT> = ({
  addVideo,
  editMode,
  experience,
  name,
  onBack,
  removeVideo,
  sessionNumber,
  setEditMode,
  showClipsBrowser,
  videosRef,
  // TODO: No need to worry now, as it is not stable code... should be refactored
  // later, when things settle down enough.
  // eslint-disable-next-line complexity
}) => {
  const fallbackDropZoneRef = useRef<HTMLDivElement>(null);

  const [movingCard, setMovingCard] = useState<number>();

  const [dropZone, setDropZone] = useState<number>();

  const [dropZoneHover, setDropZoneHover] = useState(false);

  const [
    videoBeingDragged,
    setVideoBeingDragged,
  ] = useGlobalState('ui.videoBeingDragged');

  const videoCards: ReactNode[] = [];

  let totalDurationMins = 0;

  const seenIntents = new Set<string>();
  const allIntents: string[] = [];

  const onDrop = videoBeingDragged ? (e?: DragEvent<HTMLDivElement>) => {
    setDropZone(undefined);
    setMovingCard(undefined);
    setVideoBeingDragged(undefined);
    e?.stopPropagation();

    let target = dropZone;

    if (movingCard !== undefined) {
      if (dropZone === movingCard || dropZone === movingCard + 1) {
        return;
      }

      removeVideo(movingCard);
      if (target && target > movingCard) --target;
    }

    addVideo(videoBeingDragged, target);
  } : undefined;

  const dropEvent = useEffectEvent(onDrop ?? (() => undefined));

  const experienceData = experience.data?.experience;
  if (experienceData) {
    for (let i = 0; i < experienceData.videos.length; ++i) {
      const video = experienceData.videos[i];
      if (!video) throw Error('Internal error');
      totalDurationMins += video.durationMin ?? 0;

      const { intent } = video;
      if (intent) {
        if (!seenIntents.has(intent)) {
          seenIntents.add(intent);
          allIntents.push(intent);
        }
      }

      if (showClipsBrowser && i === dropZone && (
        movingCard === undefined || (i !== movingCard && i !== movingCard + 1)
      )) {
        videoCards.push(
          <DropZone
            key="drop-zone"
            onDrop={onDrop}
          />,
        );
      }

      videoCards.push(
        <VideoCard
          draggable={!!editMode && showClipsBrowser}
          droppable
          index={editMode ? i + 1 : undefined}
          key={`${i}/${getId(video)}`}
          onDragStart={editMode && showClipsBrowser ? () => {
            setMovingCard(i);
          } : undefined}
          onDrop={onDrop}
          onDropZone={(zone) => {
            setDropZone(zone === 'AFTER' ? i + 1 : i);
          }}
          onRemove={editMode && showClipsBrowser ? () => {
            removeVideo(i);
          } : undefined}
          video={video}
        />,
      );
    }
  }

  const intentNodes: ReactNode[] = allIntents
    .slice(0, MAX_VISIBLE_INTENTS)
    .map((intent) => <div key={intent} styleName="intent">{intent}</div>);
  const hiddenIntentsCount = allIntents.length - MAX_VISIBLE_INTENTS;
  if (hiddenIntentsCount > 0) {
    intentNodes.push(
      <div key="more-intents" styleName="intent">{`+${hiddenIntentsCount}`}</div>,
    );
  }

  if (
    showClipsBrowser
    && dropZone !== undefined
    && dropZone === experienceData?.videos.length
    && movingCard !== experienceData.videos.length - 1
  ) {
    videoCards.push(
      <DropZone
        key="drop-zone"
        onDrop={onDrop}
      />,
    );
  }

  useEffect(() => {
    const dragEndListener = () => {
      setDropZone(undefined);
    };

    const clearHoverListener = () => {
      setDropZoneHover(false);
    };

    const unsubA = addTouchDropListener((x: number, y: number) => {
      const rect = fallbackDropZoneRef.current?.getBoundingClientRect();
      if (dropZone === undefined && isInsideRect([x, y], rect)) {
        dropEvent();
      }

      setDropZone(undefined);
    });

    const unsubB = addTouchCancelListener(() => {
      setDropZone(undefined);
      setDropZoneHover(false);
    });

    document.addEventListener('dragend', dragEndListener);
    document.addEventListener('dragover', dragEndListener);
    document.addEventListener('dragend', clearHoverListener);
    document.addEventListener('drop', clearHoverListener);
    return () => {
      document.removeEventListener('dragend', dragEndListener);
      document.removeEventListener('dragover', dragEndListener);
      document.removeEventListener('dragend', clearHoverListener);
      document.removeEventListener('drop', clearHoverListener);
      unsubA();
      unsubB();
    };
  }, [dropZone]);

  return (
    <div styleName="container">
      {editMode && onBack ? (
        <Button onClick={onBack} theme={backButtonTheme}>
          <IconArrowLeft />
          <span styleName="backLabel">Back to experiences</span>
        </Button>
      ) : null}
      <div styleName="head">
        <div styleName="headMain">
          <h3 styleName="subTitle">
            {`${Math.ceil(totalDurationMins)} Minutes flow for`}
          </h3>
          <div styleName="titleRow">
            <h1 styleName="title">
              {
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                name || 'Untitled'
              }
            </h1>
            <Button onClick={setEditMode} theme={iconButtonTheme}>
              <IconEdit />
            </Button>
          </div>
        </div>
        {editMode && sessionNumber !== undefined ? (
          <div styleName="sessionBlock">
            <div styleName="sessionLabel">Session</div>
            <div styleName="sessionNumber">{sessionNumber}</div>
          </div>
        ) : null}
      </div>
      {intentNodes.length ? (
        <div styleName="intentsBlock">
          {intentNodes}
        </div>
      ) : null}
      {editMode && showClipsBrowser ? (
        <div
          onDragLeave={videoBeingDragged ? () => {
            setDropZoneHover(false);
          } : undefined}
          onDragOver={videoBeingDragged ? (e) => {
            e.preventDefault();
            setDropZoneHover(true);
          } : undefined}
          onDrop={(e) => {
            setDropZoneHover(false);
            onDrop?.(e);
          }}
          styleName={dropZoneHover ? 'defaultDropZone hover' : 'defaultDropZone'}
        >
          + Drag and drop video here
        </div>
      ) : null}
      <div
        onDragOver={videoBeingDragged ? (e) => {
          e.preventDefault();
        } : undefined}

        // TODO: This is over-simplified... IRL we want to track
        // the dragging pointer position, indicate visually where
        // the video being dragged will be dropped, and drop it there.
        //
        // Actually... see how it was done in the previous version of
        // the videos timeline - drop handlers on each video card, etc.
        onDrop={onDrop}

        ref={fallbackDropZoneRef}
        styleName="videos"
      >
        <div ref={videosRef} styleName="videoCards">
          {videoCards}
        </div>
      </div>
    </div>
  );
};
